import { Box, Card, CardContent, Typography, useTheme } from '@mui/material'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface ScoreGaugeProps {
  title: string
  score: number | null
  maxScore?: number
  unavailable?: boolean
  errorMessage?: string
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
  subtitle?: string
  icon?: React.ReactNode
  details?: React.ReactNode
}

export const ScoreGauge: React.FC<ScoreGaugeProps> = ({
  title,
  score,
  maxScore = 100,
  unavailable = false,
  errorMessage,
  riskLevel,
  subtitle,
  icon,
  details,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  
  const { angle, color, riskText } = useMemo(() => {
    if (errorMessage) {
      return { angle: -90, color: '#f44336', riskText: errorMessage }
    }

    if (unavailable || score === null) {
      return { angle: -90, color: '#9e9e9e', riskText: t('fraudFlags.risk.notAvailable', 'N/A') }
    }

    const normalizedScore = (score / maxScore) * 100
    const gaugeAngle = -90 + (normalizedScore * 180) / 100

    let gaugeColor = '#4caf50' // Green for low risk
    let risk: string = t('fraudFlags.risk.low', 'LOW RISK')

    if (normalizedScore >= 75) {
      gaugeColor = '#f44336' // Red for high risk
      risk = t('fraudFlags.risk.high', 'HIGH RISK')
    } else if (normalizedScore >= 50) {
      gaugeColor = '#ff9800' // Orange for medium risk  
      risk = t('fraudFlags.risk.medium', 'MEDIUM RISK')
    } else if (normalizedScore >= 25) {
      gaugeColor = '#ffc107' // Yellow for moderate risk
      risk = t('fraudFlags.risk.moderate', 'MODERATE RISK')
    }

    if (riskLevel) {
      switch (riskLevel) {
        case 'LOW':
          gaugeColor = '#4caf50'
          risk = t('fraudFlags.risk.low', 'LOW RISK')
          break
        case 'MEDIUM':
          gaugeColor = '#ff9800'
          risk = t('fraudFlags.risk.medium', 'MEDIUM RISK')
          break
        case 'HIGH':
          gaugeColor = '#f44336'
          risk = t('fraudFlags.risk.high', 'HIGH RISK')
          break
        case 'CRITICAL':
          gaugeColor = '#d32f2f'
          risk = t('fraudFlags.risk.critical', 'CRITICAL RISK')
          break
      }
    }

    return { angle: gaugeAngle, color: gaugeColor, riskText: risk }
  }, [score, maxScore, unavailable, errorMessage, riskLevel, t])

  return (
    <Card sx={{ height: '100%', overflow: 'hidden' }}>
      <Box
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#2c3e50',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {icon && <Box sx={{ color: '#fff', display: 'flex', alignItems: 'center' }}>{icon}</Box>}
        <Typography variant="h6" sx={{ fontWeight: 500, color: '#fff', fontSize: '1rem' }}>
          {title}
        </Typography>
      </Box>
      <CardContent
        sx={{ backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f7fa' }}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <svg width="200" height="120" viewBox="0 0 200 120">
            {/* Background arc */}
            <path
              d="M 15 100 A 85 85 0 0 1 185 100"
              fill="none"
              stroke={theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#e0e0e0'}
              strokeWidth="27"
              strokeLinecap="round"
            />

            {/* Colored segments - wider segments */}
            <path
              d="M 15 100 A 85 85 0 0 1 50 30"
              fill="none"
              stroke="#4caf50"
              strokeWidth="27"
              strokeLinecap="round"
              opacity="0.3"
            />
            <path
              d="M 50 30 A 85 85 0 0 1 100 15"
              fill="none"
              stroke="#ffc107"
              strokeWidth="27"
              strokeLinecap="round"
              opacity="0.3"
            />
            <path
              d="M 100 15 A 85 85 0 0 1 145 28"
              fill="none"
              stroke="#ff9800"
              strokeWidth="27"
              strokeLinecap="round"
              opacity="0.3"
            />
            <path
              d="M 145 28 A 80 80 0 0 1 185 100"
              fill="none"
              stroke="#f44336"
              strokeWidth="27"
              strokeLinecap="round"
              opacity="0.3"
            />

            {/* Score labels */}
            <circle cx="15" cy="103" r="13" fill={'#4caf50'} opacity="1" />
            <text
              x="15"
              y="108"
              fontSize="12"
              fill={theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#666'}
              textAnchor="middle"
            >
              0
            </text>
            <circle cx="100" cy="15" r="13" fill={'#ff9800'} opacity="1" />
            <text
              x="100"
              y="20"
              fontSize="12"
              fill={theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#666'}
              textAnchor="middle"
            >
              {maxScore / 2}
            </text>
            <circle cx="185" cy="103" r="13" fill={'#f44336'} opacity="1" />
            <text
              x="185"
              y="108"
              fontSize="12"
              fill={theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#666'}
              textAnchor="middle"
            >
              {maxScore}
            </text>

            {/* Needle */}
            {!unavailable && !errorMessage && (
              <g transform={`translate(100, 100)`}>
                <line
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="-70"
                  stroke={theme.palette.mode === 'dark' ? theme.palette.text.primary : '#2c3e50'}
                  strokeWidth="3"
                  strokeLinecap="round"
                  transform={`rotate(${angle})`}
                />
                <circle
                  cx="0"
                  cy="0"
                  r="8"
                  fill={theme.palette.mode === 'dark' ? theme.palette.text.primary : '#2c3e50'}
                />
              </g>
            )}
          </svg>

          {/* Score display */}
          <Typography
            variant="h3"
            sx={{
              mt: -2,
              fontWeight: 'bold',
              color: unavailable
                ? theme.palette.text.disabled
                : theme.palette.mode === 'dark'
                  ? theme.palette.text.primary
                  : '#2c3e50',
            }}
          >
            {errorMessage ? 'N/A' : unavailable ? 'N/A' : score}
          </Typography>

          {/* Risk level */}
          <Typography
            variant="body2"
            sx={{
              mt: 1,
              px: 2,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: unavailable ? '#e0e0e0' : color,
              color: '#fff',
              fontWeight: 500,
            }}
          >
            {riskText}
          </Typography>

          {/* Subtitle */}
          {subtitle && (
            <Typography variant="caption" sx={{ mt: 1, color: theme.palette.text.secondary }}>
              {subtitle}
            </Typography>
          )}

          {/* Details */}
          {details && <Box sx={{ mt: 2, width: '100%' }}>{details}</Box>}
        </Box>
      </CardContent>
    </Card>
  )
}